import React from 'react';
import { Card, KPICard, Avatar } from '@/components/ui';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { ConstructionContract } from '../types/construction-contracts.types';

interface OverviewTabProps {
  item: ConstructionContract;
  onLegalEdit: () => void;
  onTeamEdit: () => void;
}

export function OverviewTab({ item, onLegalEdit, onTeamEdit }: OverviewTabProps) {
  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard
          title="tổng giá trị hợp đồng"
          value={formatCurrency(item.contract_value)}
          icon="ri-money-dollar-circle-line"
          variant="primary"
        />
        <KPICard
          title="tạm ứng"
          value={formatCurrency(item.advance_payment_amount)}
          icon="ri-hand-coin-line"
          variant="info"
        />
        <KPICard
          title="phí bảo hành"
          value={item.retention_rate}
          icon="ri-shield-check-line"
          variant="warning"
          suffix="%"
        />
      </div>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <i className="ri-information-line text-os-primary" />
            Thông tin pháp lý
          </h3>
          <AuthButton module="boq" action="update" variant="primary" size="sm" icon="ri-edit-line" onClick={onLegalEdit}>
            Cập nhật
          </AuthButton>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400  tracking-widest">Chủ đầu tư (Client)</span>
            <div className="text-sm font-bold text-slate-700">{item.client_name || '---'}</div>
          </div>
          <div className="space-y-1">
            <span className="text-[10px] font-bold text-slate-400  tracking-widest">Nhà thầu (Contractor)</span>
            <div className="text-sm font-bold text-os-primary">{item.contractor_name || '---'}</div>
          </div>
          <div className="flex justify-between border-b border-slate-50 pb-2 md:col-span-1">
            <span className="text-sm text-slate-500">Ngày ký kết</span>
            <span className="text-sm font-bold text-slate-700">
              {item.signed_date ? new Date(item.signed_date).toLocaleDateString('vi-VN') : '---'}
            </span>
          </div>
          <div className="flex justify-between border-b border-slate-50 pb-2 md:col-span-1">
            <span className="text-sm text-slate-500">Thời gian bảo hành</span>
            <span className="text-sm font-bold text-slate-700">{item.warranty_months || 0} tháng</span>
          </div>
        </div>
        <div className="mt-6">
          <label className="block text-[10px] font-bold text-slate-400 mb-2  tracking-widest">Tóm tắt phạm vi thi công</label>
          <p className="text-sm text-slate-600 bg-slate-50 p-4 rounded-2xl border border-slate-100 leading-relaxed">
            {item.scope_summary || 'Chưa có thông tin tóm tắt phạm vi hợp đồng.'}
          </p>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <i className="ri-user-settings-line text-slate-400" />
            Đội ngũ phụ trách
          </h3>
          <AuthButton module="boq" action="update" variant="primary" size="sm" icon="ri-edit-line" onClick={onTeamEdit}>
            Cập nhật
          </AuthButton>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-100">
            <Avatar name={item.pm?.full_name} src={item.pm?.avatar_url} size="xs" />
            <div>
              <p className="text-[10px] text-slate-400 font-bold  tracking-tighter">PM</p>
              <p className="text-xs font-bold text-slate-700">{item.pm?.full_name || '---'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-100">
            <Avatar name={item.site_manager?.full_name} src={item.site_manager?.avatar_url} size="xs" />
            <div>
              <p className="text-[10px] text-slate-400 font-bold  tracking-tighter">SM</p>
              <p className="text-xs font-bold text-slate-700">{item.site_manager?.full_name || '---'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-2xl border border-slate-100">
            <Avatar name={item.qs?.full_name} src={item.qs?.avatar_url} size="xs" />
            <div>
              <p className="text-[10px] text-slate-400 font-bold  tracking-tighter">QS</p>
              <p className="text-xs font-bold text-slate-700">{item.qs?.full_name || '---'}</p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
